"use client"

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { getTokens } from "@/lib/auth"
import { useUser } from "@/components/user-context"
import { useAttachmentUploads, type AttachmentUploads } from "@/components/bugfix-agent/attachments"
import { feedbackApi, uploadFeedbackFile } from "@/modules/feedback/lib/client/api"
import { collectContext } from "@/modules/feedback/lib/client/context"
import { captureDomScreenshot, captureMode, startDomRecording, type CaptureMode } from "@/modules/feedback/lib/client/dom-capture"
import { CaptureCancelled, openScreenshotSource, startScreenRecording, type RecorderHandle, type RecordingResult, type ScreenshotSource } from "@/modules/feedback/lib/client/recorder"
import { captureViewportPng, startPageRecording } from "@/modules/feedback/lib/client/page-capture"
import { MAX_FEEDBACK_ATTACHMENTS, type Feedback, type FeedbackContext, type FeedbackKind, type FeedbackRating, type MyRating } from "@/modules/feedback/lib/shared/types"
import { FeedbackDialog } from "./feedback-dialog"
import { ScreenshotTrigger } from "./capture-overlay"
import { RecordingIndicator } from "./recording-indicator"

/** Ответ копилота, который оценивают */
export interface CopilotTarget {
  dialogId: string | null
  /** id ответа на сервере; нет — снимок соберётся из getMessages */
  messageId: string | null
  dialogTitle?: string | null
  /** Сообщения ветки до ответа включительно — на случай, если диалога нет в базе */
  getMessages: () => unknown[]
  scope?: string | null
  mode?: { knowledgeBase?: boolean; webSearch?: boolean } | null
}

export interface FeedbackDraft {
  kind: FeedbackKind
  feedbackId: string | null
  number: number | null
  rating: FeedbackRating | null
  tags: string[]
  comment: string
  target: CopilotTarget | null
  context: FeedbackContext | null
}

export const RATINGS_CHANGED_EVENT = "casper:feedback-ratings"
/** Отзыв отправлен из формы: «Разработка → Обратная связь» обновляет список */
export const FEEDBACK_SUBMITTED_EVENT = "casper:feedback-submitted"
export interface FeedbackSubmittedDetail {
  feedback: Feedback
  created: boolean
}
export interface RatingsChangedDetail {
  dialogId: string | null
  messageId: string | null
  rating: MyRating | null
}

/** capturing — выбор экрана, aiming — ждём кнопку «Снять», shooting — снимаем кадр */
type CaptureState = "idle" | "capturing" | "aiming" | "shooting" | "recording"

type AddFiles = (files: File[]) => void

/** Скриншот и запись экрана для любых форм с вложениями (отзыв, багфикс-агент) */
export interface ScreenCapture {
  mode: CaptureMode | null
  /** Идёт выбор экрана или запись: формы-диалоги на это время закрываются, чтобы не попасть в кадр */
  active: boolean
  recording: boolean
  mic: boolean
  setMic: (v: boolean) => void
  screenshot: (add: AddFiles) => void
  record: (add: AddFiles) => void
}

interface FeedbackApi {
  openProduct: () => void
  /** Поставить оценку ответу и открыть форму с подробностями */
  rateAnswer: (target: CopilotTarget, rating: FeedbackRating, existing?: MyRating | null) => Promise<void>
  /** Открыть сохранённую оценку для правки */
  editAnswer: (target: CopilotTarget, existing: MyRating) => Promise<void>
  /** Снять оценку вместе с отзывом */
  withdrawAnswer: (target: CopilotTarget, existing: MyRating) => Promise<void>
  recording: boolean
  capture: ScreenCapture
  /** Форма закрыта и можно снять экран жестом */
  canGesture: boolean
  /** Сразу снять текущий экран и открыть отзыв с этим снимком */
  captureScreen: () => Promise<void>
  /** Сразу начать запись экрана. Микрофон уже запрошен жестом, если он есть. */
  startQuickRecording: (audio: { stream: MediaStream | null; error: string | null }) => Promise<void>
}

const Ctx = createContext<FeedbackApi | null>(null)

export function useFeedback(): FeedbackApi | null {
  return useContext(Ctx)
}

export function useScreenCapture(): ScreenCapture | null {
  return useContext(Ctx)?.capture ?? null
}

/** Закрыть диалог на время выбора экрана и записи: состояние формы остаётся у владельца */
export function useCaptureHidden(): boolean {
  return useScreenCapture()?.active ?? false
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

const fmtDuration = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`

const emptyDraft = (kind: FeedbackKind): FeedbackDraft => ({ kind, feedbackId: null, number: null, rating: null, tags: [], comment: "", target: null, context: null })

const emitRatings = (detail: RatingsChangedDetail) => window.dispatchEvent(new CustomEvent<RatingsChangedDetail>(RATINGS_CHANGED_EVENT, { detail }))

const asMyRating = (f: Feedback): MyRating => ({ feedbackId: f.id, messageId: f.copilot?.messageId ?? "", rating: f.rating, hasDetails: !!(f.comment || f.attachments.length || f.tags.length) })

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  const { userWorkspace } = useUser()
  const [dialogOpen, setDialogOpen] = useState(false)
  const dialogOpenRef = useRef(dialogOpen)
  dialogOpenRef.current = dialogOpen
  const [captureState, setCaptureState] = useState<CaptureState>("idle")
  const [mode, setMode] = useState<CaptureMode | null>(null)
  const [draft, setDraft] = useState<FeedbackDraft>(() => emptyDraft("product"))
  const [sending, setSending] = useState(false)
  const [mic, setMic] = useState(true)
  const [recorder, setRecorder] = useState<RecorderHandle | null>(null)
  const recorderRef = useRef<RecorderHandle | null>(null)
  recorderRef.current = recorder
  const uploads: AttachmentUploads = useAttachmentUploads(MAX_FEEDBACK_ATTACHMENTS, uploadFeedbackFile)
  const uploadsRef = useRef(uploads)
  uploadsRef.current = uploads
  const shotRef = useRef<{ source: ScreenshotSource; add: AddFiles } | null>(null)
  const captureRef = useRef<CaptureState>("idle")
  captureRef.current = captureState

  useEffect(() => setMode(captureMode()), [])

  const workspace = useMemo(() => ({ workspaceId: userWorkspace?.workspace?.id ?? null, workspaceName: userWorkspace?.workspace?.name ?? null }), [userWorkspace?.workspace?.id, userWorkspace?.workspace?.name])

  const loggedIn = () => {
    if (getTokens()?.accessToken) return true
    toast.error("Войдите, чтобы отправить отзыв")
    return false
  }

  const close = useCallback(() => {
    setDialogOpen(false)
    uploadsRef.current.reset()
    setDraft(emptyDraft("product"))
  }, [])

  const openProduct = useCallback(() => {
    if (!loggedIn()) return
    // Кнопку отзыва можно нажать и во время записи: ролик останавливается и прикладывается к форме.
    if (captureRef.current === "recording" && recorderRef.current) {
      recorderRef.current.stop()
      return
    }
    if (captureRef.current !== "idle") return
    uploadsRef.current.reset()
    setDraft(emptyDraft("product"))
    setDialogOpen(true)
    void collectContext(workspace).then((context) => setDraft((d) => (d.kind === "product" ? { ...d, context } : d)))
  }, [workspace])

  const copilotContext = useCallback((target: CopilotTarget) => collectContext({ ...workspace, copilotScope: target.scope ?? null, copilotMode: target.mode ?? null }), [workspace])

  const openWith = useCallback((target: CopilotTarget, f: Feedback, context: FeedbackContext) => {
    setDraft({ kind: "copilot", feedbackId: f.id, number: f.number, rating: f.rating, tags: f.tags, comment: f.comment, target, context })
    uploadsRef.current.preset(f.attachments)
    setDialogOpen(true)
  }, [])

  const rateAnswer = useCallback(
    async (target: CopilotTarget, rating: FeedbackRating, existing?: MyRating | null) => {
      if (!loggedIn()) return
      try {
        const context = await copilotContext(target)
        const f = existing?.feedbackId
          ? await feedbackApi.update(existing.feedbackId, { rating })
          : await feedbackApi.create({
              kind: "copilot",
              rating,
              context,
              copilot: { dialogId: target.dialogId, messageId: target.messageId, dialogTitle: target.dialogTitle ?? null, messages: target.getMessages() },
            })
        emitRatings({ dialogId: target.dialogId, messageId: target.messageId, rating: asMyRating(f) })
        openWith(target, f, context)
      } catch (e) {
        toast.error((e as Error).message || "Не удалось сохранить оценку")
      }
    },
    [copilotContext, openWith],
  )

  const editAnswer = useCallback(
    async (target: CopilotTarget, existing: MyRating) => {
      if (!loggedIn()) return
      try {
        const [f, context] = await Promise.all([feedbackApi.get(existing.feedbackId), copilotContext(target)])
        openWith(target, f, f.context ?? context)
      } catch (e) {
        toast.error((e as Error).message || "Не удалось открыть оценку")
      }
    },
    [copilotContext, openWith],
  )

  const withdrawAnswer = useCallback(async (target: CopilotTarget, existing: MyRating) => {
    try {
      await feedbackApi.remove(existing.feedbackId)
      emitRatings({ dialogId: target.dialogId, messageId: target.messageId, rating: null })
      toast.success("Оценка отменена")
    } catch (e) {
      toast.error((e as Error).message || "Не удалось отменить оценку")
    }
  }, [])

  const submit = useCallback(async () => {
    const u = uploadsRef.current
    if (u.uploading) return toast.info("Дождитесь загрузки файлов")
    setSending(true)
    try {
      const input = { rating: draft.rating, tags: draft.tags, comment: draft.comment, attachmentIds: u.ids }
      const f = draft.feedbackId
        ? await feedbackApi.update(draft.feedbackId, input)
        : await feedbackApi.create({ kind: draft.kind, ...input, context: draft.context ?? (await collectContext(workspace)) })
      if (f.kind === "copilot") emitRatings({ dialogId: draft.target?.dialogId ?? null, messageId: draft.target?.messageId ?? null, rating: asMyRating(f) })
      window.dispatchEvent(new CustomEvent<FeedbackSubmittedDetail>(FEEDBACK_SUBMITTED_EVENT, { detail: { feedback: f, created: !draft.feedbackId } }))
      toast.success(`Спасибо! Отзыв #${f.number} отправлен`, { description: "Команда разработки его увидит" })
      close()
    } catch (e) {
      toast.error((e as Error).message || "Не удалось отправить отзыв")
    } finally {
      setSending(false)
    }
  }, [draft, workspace, close])

  const endShot = useCallback(() => {
    shotRef.current?.source.close()
    shotRef.current = null
    setCaptureState("idle")
  }, [])

  const screenshot = useCallback(
    async (add: AddFiles) => {
      if (captureRef.current !== "idle") return
      const how = captureMode()
      if (!how) return toast.error("Браузер не умеет делать снимок экрана")
      setCaptureState("capturing")
      try {
        const source: ScreenshotSource = how === "display" ? await openScreenshotSource(endShot) : { grab: captureDomScreenshot, close: () => {} }
        shotRef.current = { source, add }
        setCaptureState("aiming")
      } catch (e) {
        if (!(e instanceof CaptureCancelled)) toast.error((e as Error).message)
        setCaptureState("idle")
      }
    },
    [endShot],
  )

  const shoot = useCallback(async () => {
    const shot = shotRef.current
    if (!shot) return
    // кнопка «Снять» успевает исчезнуть с экрана до кадра
    setCaptureState("shooting")
    await wait(150)
    try {
      shot.add([await shot.source.grab()])
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      endShot()
    }
  }, [endShot])

  const record = useCallback(
    async (add: AddFiles) => {
      if (captureRef.current !== "idle") return
      const how = captureMode()
      if (!how) return toast.error("Браузер не умеет записывать экран")
      setCaptureState("capturing")
      try {
        const opts = {
          mic,
          onDone: (r: RecordingResult) => {
            setRecorder(null)
            add([r.file])
            setCaptureState("idle")
            const mb = (r.file.size / 1024 / 1024).toFixed(1)
            toast.success(`Видео записано: ${fmtDuration(r.durationSec)}, ${mb} МБ`, {
              description: [r.hasMic ? "с микрофоном" : "без микрофона", r.hasSystemAudio ? "со звуком интерфейса" : "без звука интерфейса"].join(", "),
            })
          },
          onError: (message: string) => {
            setRecorder(null)
            setCaptureState("idle")
            toast.error(message)
          },
        }
        if (how === "dom") await wait(600)
        const handle = how === "display" ? await startScreenRecording(opts) : await startDomRecording(opts)
        if (mic && handle.micError) toast.warning(`Микрофон не подключён: ${handle.micError}`, { description: "Запись идёт без голоса" })
        setRecorder(handle)
        setCaptureState("recording")
      } catch (e) {
        if (!(e instanceof CaptureCancelled)) toast.error((e as Error).message)
        setCaptureState("idle")
      }
    },
    [mic],
  )

  const capture = useMemo<ScreenCapture>(
    () => ({ mode, active: captureState !== "idle", recording: captureState === "recording", mic, setMic, screenshot: (add) => void screenshot(add), record: (add) => void record(add) }),
    [mode, captureState, mic, screenshot, record],
  )

  const captureScreen = useCallback(async () => {
    if (!loggedIn()) return
    if (dialogOpenRef.current || captureRef.current !== "idle") return
    try {
      const file = await captureViewportPng()
      if (dialogOpenRef.current || captureRef.current !== "idle") return
      uploadsRef.current.reset()
      setDraft(emptyDraft("product"))
      uploadsRef.current.add([file])
      setDialogOpen(true)
      void collectContext(workspace).then((context) => setDraft((d) => (d.kind === "product" && !d.feedbackId ? { ...d, context } : d)))
    } catch (e) {
      toast.error((e as Error).message || "Не удалось сделать снимок")
    }
  }, [workspace])

  const startQuickRecording = useCallback(async (audio: { stream: MediaStream | null; error: string | null }) => {
    if (!loggedIn()) {
      audio.stream?.getTracks().forEach((t) => t.stop())
      return
    }
    if (dialogOpenRef.current || captureRef.current !== "idle") {
      audio.stream?.getTracks().forEach((t) => t.stop())
      return
    }
    setMic(true)
    uploadsRef.current.reset()
    setDraft(emptyDraft("product"))
    void collectContext(workspace).then((context) => setDraft((d) => (d.kind === "product" && !d.feedbackId ? { ...d, context } : d)))
    setCaptureState("capturing")
    try {
      const handle = await startPageRecording({
        mic: audio.stream,
        micError: audio.error,
        onDone: (r) => {
          setRecorder(null)
          uploadsRef.current.add([r.file])
          setCaptureState("idle")
          setDialogOpen(true)
          const mb = (r.file.size / 1024 / 1024).toFixed(1)
          toast.success(`Видео записано: ${fmtDuration(r.durationSec)}, ${mb} МБ`, {
            description: r.hasMic ? "с микрофоном" : "без микрофона",
          })
        },
        onError: (message) => {
          setRecorder(null)
          setCaptureState("idle")
          toast.error(message)
        },
      })
      if (!handle.hasMic && audio.error) toast.warning(`Микрофон не подключён: ${audio.error}`, { description: "Запись идёт без голоса" })
      setRecorder(handle)
      setCaptureState("recording")
    } catch (e) {
      audio.stream?.getTracks().forEach((t) => t.stop())
      setCaptureState("idle")
      toast.error((e as Error).message || "Не удалось начать запись")
    }
  }, [workspace])

  const api = useMemo<FeedbackApi>(
    () => ({
      openProduct,
      rateAnswer,
      editAnswer,
      withdrawAnswer,
      recording: captureState === "recording",
      capture,
      canGesture: !dialogOpen && captureState === "idle",
      captureScreen,
      startQuickRecording,
    }),
    [openProduct, rateAnswer, editAnswer, withdrawAnswer, captureState, capture, dialogOpen, captureScreen, startQuickRecording],
  )

  return (
    <Ctx.Provider value={api}>
      {children}
      <FeedbackDialog open={dialogOpen && captureState === "idle"} draft={draft} onDraft={setDraft} uploads={uploads} sending={sending} onClose={close} onSubmit={submit} />
      {captureState === "aiming" && <ScreenshotTrigger onShoot={() => void shoot()} onCancel={endShot} />}
      {captureState === "recording" && recorder && <RecordingIndicator recorder={recorder} />}
    </Ctx.Provider>
  )
}
